import { Injectable } from '@angular/core';
import { EChartsOption } from 'echarts';
import { getStyleProp } from 'ngx-core-services';
import { EvChargeSession } from '../evcharge-telemetry/evcharge-telemetry.model';
import { AggregatedEnergyData, DailyAggregation } from '../evcharge-energy-aggregation/evcharge-energy-aggregation.model';
import { buildDataZoomSlider } from '../utils';

@Injectable({
  providedIn: 'root'
})
export class EvchargeSessionChartService {

  /**
   * Builds a combined chart showing both power and energy for a session
   * Useful for comprehensive session analysis
   */
  buildCombinedSessionChart(
    session: EvChargeSession,
    sources: ('power' | 'energy')[] = ['power','energy']
  ): EChartsOption {
    const series: any[] = [];
    const hasPower = sources.includes('power');
    const hasEnergy = sources.includes('energy');

    if (sources.includes('power')) {
      series.push({
        name: 'Power',
        data: session.rawData.map((value) => [
          value.TS * 1000,
          (value.CP || 0) / 1000
        ]),
        type: 'line',
        yAxisIndex: 0,
        smooth: true,
        showAllSymbol: true,
        symbol: 'circle',
        symbolSize: 6,
        hoverAnimation: true,
        emphasis: { focus: 'series' },
        color: getStyleProp('--blue-500'),
        itemStyle: { color: getStyleProp('--blue-500') },
      });
    }

    if (sources.includes('energy')) {
      series.push({
        name: 'Energy (cumulative)',
        data: session.rawData.map((value) => [
          value.TS * 1000,
          (value.SEM || 0)
        ]),
        type: 'line',
        yAxisIndex: hasPower && hasEnergy ? 1 : 0,
        smooth: true,
        showAllSymbol: true,
        symbol: 'circle',
        symbolSize: 6,
        hoverAnimation: true,
        emphasis: { focus: 'series' },
        color: getStyleProp('--green-400'),
        itemStyle: { color: getStyleProp('--green-400') },
      });
    }

    return {
      title: {
        left: 'center',
        top: 12,
        textStyle: {
          color: getStyleProp('--grey-400'),
          fontSize: 16,
          fontWeight: 500,
        },
      },
      legend: { show: false },
      grid: {
        show: false,
        top: '80',
        bottom: '80',
        left: '60',
        right: '60',
      },
      dataZoom: buildDataZoomSlider().map(dz => ({
        ...dz,
        labelFormatter: (value: number) => {
          const d = new Date(value);
          const h = d.getHours();
          const m = d.getMinutes();
          return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`;
        },
      })),
      tooltip: {
        confine: true,
        trigger: 'axis',
        triggerOn: 'mousemove|click',
        formatter: (elements: any): string => {
          const date = new Date(elements[0].data[0]);
          const hour = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');

          const headerTemplate = `
            <div class="fx-row text-accent-700 fx-align-center fx-justify-between tw-text-sm p-1 bg-accent-100 radius-t-4">
              <div class="tw-font-sm ml-auto">${hour}:${minutes} <span class="tw-text-xs tw-font-bold">${Number(hour) < 12 ? 'AM' : 'PM'}</span></div>
            </div>
          `;

          const detailsTemplate = elements.map((element: any) => {
            const { data: [, valueY], seriesName, color } = element;
            const unit = seriesName.includes('Power') ? 'kW' : 'kWh';
            const decimals = unit === 'kW' ? 2 : 3;
            return `
              <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
                <div class="tw-font-medium text-style-italic mr-5 fx-row">
                  <div class="s-4 rounded mr-3" style="background: ${color}"></div>
                  ${seriesName}
                </div>
                <div class="tw-font-normal">${valueY.toFixed(decimals)} <span class="tw-text-xs tw-font-bold">${unit}</span></div>
              </div>`;
          }).join('');

          return `${headerTemplate}${detailsTemplate}`;
        },
        backgroundColor: '#ffffff',
        borderColor: getStyleProp('--grey-300'),
        shadowBlur: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadius: 4,
        padding: 0,
        textStyle: { color: getStyleProp('--grey-400') },
      },
      xAxis: {
        type: 'time',
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          color: getStyleProp('--grey-400'),
          fontWeight: 'bold',
          showMinLabel: false,
          showMaxLabel: false,
          formatter: '{H}:{mm}',
          hideOverlap: true,
          rotate: 45,
          margin: 10,
        },
        boundaryGap: ['5%', '5%'],
      },
      yAxis: hasPower && hasEnergy
        ? [
            {
              type: 'value',
              name: 'kW',
              nameLocation: 'end',
              nameTextStyle: { color: getStyleProp('--blue-500'), fontWeight: 'bold' },
              axisLabel: { showMinLabel: false, showMaxLabel: false, color: getStyleProp('--blue-500'), fontWeight: 'bold', formatter: (value: number) => value.toFixed(1) },
              axisTick: { show: false },
              splitLine: { show: false },
            },
            {
              type: 'value',
              name: 'kWh',
              nameLocation: 'end',
              nameTextStyle: { color: getStyleProp('--green-400'), fontWeight: 'bold' },
              axisLabel: { showMinLabel: false, showMaxLabel: false, color: getStyleProp('--green-400'), fontWeight: 'bold', formatter: (value: number) => value.toFixed(2) },
              axisTick: { show: false },
              splitLine: { show: false },
            },
          ]
        : hasPower
        ? [
            {
              type: 'value',
              name: 'kW',
              nameLocation: 'end',
              nameTextStyle: { color: getStyleProp('--blue-500'), fontWeight: 'bold' },
              axisLabel: { showMinLabel: false, showMaxLabel: false, color: getStyleProp('--blue-500'), fontWeight: 'bold', formatter: (value: number) => value.toFixed(1) },
              axisTick: { show: false },
              splitLine: { show: true, lineStyle: { color: 'var(--divider-color)', type: 'dashed' } },
            },
            { type: 'value', show: false }
          ]
        : [
            {
              type: 'value',
              name: 'kWh',
              nameLocation: 'end',
              nameTextStyle: { color: getStyleProp('--green-400'), fontWeight: 'bold' },
              axisLabel: { showMinLabel: false, showMaxLabel: false, color: getStyleProp('--green-400'), fontWeight: 'bold', formatter: (value: number) => value.toFixed(2) },
              axisTick: { show: false },
              splitLine: { show: true, lineStyle: { color: 'var(--divider-color)', type: 'dashed' } },
            },
            { type: 'value', show: false }
          ],
      series,
    };
  }

  /**
   * Build energy aggregation chart (week or month)
   * Simple vertical bar chart showing daily energy
   */
  buildEnergyAggregationChart(
    aggregatedData: AggregatedEnergyData,
    labels: { energy: string; sessions: string; date: string }
  ): EChartsOption {
    const chartData = aggregatedData.dailyData.map(d => ({
      date: d.date,
      totalEnergy: d.totalEnergy,
      sessionCount: d.sessionCount
    }));

    const isMonthMode = aggregatedData.period === 'month';

    return {
      grid: {
        left: 5,
        right: 5,
        bottom: 5,
        containLabel: true
      },
      legend: { show: false },
      tooltip: {
        confine: true,
        trigger: 'axis',
        triggerOn: 'mousemove|click',
        formatter: (elements: any): string => {
          const { dataIndex } = elements[0];
          const data = chartData[dataIndex];
          if (!data) return '';

          const date = new Date(data.date);
          const formattedDate = isMonthMode
            ? `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`
            : date.toLocaleDateString('en-US', { month: 'short' });

          const headerTemplate = `
            <div class="fx-row text-accent-700 fx-align-center fx-justify-between tw-text-sm p-1 bg-accent-100 radius-t-4">
              <div class="tw-font-sm ml-auto">${formattedDate}</div>
            </div>
          `;

          const detailsTemplate = `
            <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
              <div class="tw-font-medium text-style-italic mr-5 fx-row">
                <div class="s-4 rounded mr-3" style="background: ${getStyleProp('--green-500')}"></div>
                ${labels.energy}
              </div>
              <div class="tw-font-normal">${data.totalEnergy.toFixed(2)} <span class="tw-text-xs tw-font-bold">kWh</span></div>
            </div>
            <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
              <div class="tw-font-medium text-style-italic mr-5 fx-row">
                <div class="s-4 rounded mr-3" style="background: ${getStyleProp('--grey-400')}"></div>
                ${labels.sessions}
              </div>
              <div class="tw-font-normal">${data.sessionCount}</div>
            </div>
          `;

          return `${headerTemplate}${detailsTemplate}`;
        },
        backgroundColor: '#ffffff',
        borderColor: getStyleProp('--grey-300'),
        shadowBlur: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadius: 4,
        padding: 0,
        textStyle: { color: getStyleProp('--grey-400') },
      },
      xAxis: {
        type: 'category',
        data: chartData.map(d => {
          const date = new Date(d.date);
          return isMonthMode
            ? `${date.getDate()}`
            : date.toLocaleDateString('en-US', { month: 'short' });
        }),
        axisLine: {
          onZero: true,
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          color: getStyleProp('--grey-300'),
          fontWeight: 'bold',
          showMinLabel: true,
          showMaxLabel: true,
          hideOverlap: true,
          rotate: 45,
        },
      },
      yAxis: {
        type: 'value',
        name: 'kWh',
        nameLocation: 'end',
        nameTextStyle: {
          color: getStyleProp('--grey-500'),
          fontWeight: 'bold',
        },
        splitLine: { show: false },
        axisLabel: {
          showMinLabel: false,
          showMaxLabel: false,
          color: getStyleProp('--grey-500'),
          fontWeight: 'bold',
          formatter: (value: any) => `${Math.abs(value)}`,
        },
      },
      series: [
        {
          data: chartData.map(d => ({
            value: d.totalEnergy,
            date: d.date,
            totalEnergy: d.totalEnergy,
            sessionCount: d.sessionCount
          })),
          type: 'bar',
          color: getStyleProp('--green-500'),
          itemStyle: {
            color: getStyleProp('--green-500'),
          },
          emphasis: {
            itemStyle: {
              color: getStyleProp('--green-600'),
            },
          },
        },
      ],
    };
  }

  /**
   * Build reduced energy aggregation charts (weekly for month, half-year for year)
   */
  buildReducedEnergyAggregationCharts(
    aggregatedData: AggregatedEnergyData,
    labels: { energy: string; sessions: string; date: string }
  ): EChartsOption[] {
    const isMonthMode = aggregatedData.period === 'month';
    const dailyData = aggregatedData.dailyData;

    if (isMonthMode) {
      // Split into weeks
      return this.splitIntoWeeks(dailyData).map(weekData =>
        this.buildSingleReducedChart(weekData, labels, true)
      );
    } else {
      // Split into half years
      return this.splitIntoHalfYear(dailyData).map(halfYearData =>
        this.buildSingleReducedChart(halfYearData, labels, false)
      );
    }
  }

  private splitIntoWeeks(dailyData: DailyAggregation[]): DailyAggregation[][] {
    const weeks: DailyAggregation[][] = [];
    let currentWeek: DailyAggregation[] = [];

    dailyData.forEach((day, index) => {
      const date = new Date(day.date);
      const dayOfWeek = date.getDay();

      // Start new week on Monday (1)
      if (dayOfWeek === 1 && currentWeek.length > 0) {
        weeks.push(currentWeek);
        currentWeek = [];
      }

      currentWeek.push(day);

      // Push last week
      if (index === dailyData.length - 1 && currentWeek.length > 0) {
        weeks.push(currentWeek);
      }
    });

    return weeks;
  }

  private splitIntoHalfYear(dailyData: DailyAggregation[]): DailyAggregation[][] {
    const midPoint = Math.ceil(dailyData.length / 2);
    return [
      dailyData.slice(0, midPoint),
      dailyData.slice(midPoint)
    ];
  }

  private buildSingleReducedChart(
    data: DailyAggregation[],
    labels: { energy: string; sessions: string; date: string },
    isWeekly: boolean
  ): EChartsOption {
    return {
      grid: {
        left: 5,
        right: 5,
        bottom: 5,
        top: 40,
        containLabel: true
      },
      legend: { show: false },
      tooltip: {
        confine: true,
        trigger: 'axis',
        triggerOn: 'mousemove|click',
        formatter: (elements: any): string => {
          const { dataIndex } = elements[0];
          const item = data[dataIndex];
          if (!item) return '';

          const date = new Date(item.date);
          const formattedDate = date.toLocaleDateString('en-US', { month: 'short' });

          const headerTemplate = `
            <div class="fx-row text-accent-700 fx-align-center fx-justify-between tw-text-sm p-1 bg-accent-100 radius-t-4">
              <div class="tw-font-sm ml-auto">${formattedDate}</div>
            </div>
          `;

          const detailsTemplate = `
            <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
              <div class="tw-font-medium text-style-italic mr-5 fx-row">
                <div class="s-4 rounded mr-3" style="background: ${getStyleProp('--green-500')}"></div>
                ${labels.energy}
              </div>
              <div class="tw-font-normal">${item.totalEnergy.toFixed(2)} <span class="tw-text-xs tw-font-bold">kWh</span></div>
            </div>
            <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
              <div class="tw-font-medium text-style-italic mr-5 fx-row">
                <div class="s-4 rounded mr-3" style="background: ${getStyleProp('--grey-400')}"></div>
                ${labels.sessions}
              </div>
              <div class="tw-font-normal">${item.sessionCount}</div>
            </div>
          `;

          return `${headerTemplate}${detailsTemplate}`;
        },
        backgroundColor: '#ffffff',
        borderColor: getStyleProp('--grey-300'),
        shadowBlur: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderRadius: 4,
        padding: 0,
        textStyle: { color: getStyleProp('--grey-400') },
      },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const date = new Date(d.date);
          return isWeekly
            ? `${date.getDate()}`
            : date.toLocaleDateString('en-US', { month: 'short' });
        }),
        axisLine: {
          onZero: true,
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          color: getStyleProp('--grey-300'),
          fontWeight: 'bold',
          showMinLabel: true,
          showMaxLabel: true,
          hideOverlap: true,
          rotate: 45,
        },
      },
      yAxis: {
        type: 'value',
        name: 'kWh',
        nameLocation: 'end',
        nameTextStyle: {
          color: getStyleProp('--grey-500'),
          fontWeight: 'bold',
        },
        splitLine: { show: false },
        axisLabel: {
          showMinLabel: false,
          showMaxLabel: false,
          color: getStyleProp('--grey-500'),
          fontWeight: 'bold',
          formatter: (value: any) => `${Math.abs(value)}`,
        },
      },
      series: [
        {
          data: data.map(d => ({
            value: d.totalEnergy,
            date: d.date,
            totalEnergy: d.totalEnergy,
            sessionCount: d.sessionCount
          })),
          type: 'bar',
          color: getStyleProp('--green-500'),
          itemStyle: {
            color: getStyleProp('--green-500'),
          },
          emphasis: {
            itemStyle: {
              color: getStyleProp('--green-600'),
            },
          },
        },
      ],
    };
  }
}
